/**
 * QuoterProductImages - Mapeo de productos a imágenes
 *
 * Centraliza la gestión de imágenes para mostrar en el carrito.
 * Si no hay imagen configurada, usa un color de fondo representativo.
 *
 * @version 1.0.0
 */

const QuoterProductImages = {
  /**
   * URLs de imágenes por producto
   * Configurar con URLs del CDN de Shopify cuando estén disponibles
   * Formato: 'https://cdn.shopify.com/s/files/1/XXXX/XXXX/files/imagen.png'
   */
  IMAGES: {
    premium: null,
    holografico: null,
    economico: null,
    iman: null,
    sheets: null,
    sheets_economicas: null,
  },

  /**
   * Colores de fondo por producto (fallback cuando no hay imagen)
   */
  COLORS: {
    premium: '#3B82F6',            // Azul premium
    holografico: '#8B5CF6',        // Púrpura holográfico
    economico: '#6B7280',          // Gris económico
    iman: '#F59E0B',               // Naranja/ámbar
    sheets: '#10B981',             // Verde esmeralda
    sheets_economicas: '#14B8A6',  // Teal sheets económicas
  },

  /**
   * Nombres legibles de productos
   */
  NAMES: {
    premium: 'Sticker Premium',
    holografico: 'Holográfico',
    economico: 'Sticker Económico',
    iman: 'Imán Impreso',
    sheets: 'Sheets Laminadas',
    sheets_economicas: 'Sheets Económicas',
  },

  /**
   * Iconos SVG por producto (para usar como placeholder)
   */
  ICONS: {
    premium: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>',
    holografico: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>',
    economico: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>',
    iman: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 7v6h3v-6h2v6h3v-6h2v6h3v-6h2v6h3V7H3z"/></svg>',
    sheets: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM6 20V4h7v5h5v11H6z"/></svg>',
    sheets_economicas: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM6 20V4h7v5h5v11H6z"/></svg>',
  },

  /**
   * Imagen por defecto si el producto no existe
   */
  DEFAULT_COLOR: '#E5E7EB',
  DEFAULT_NAME: 'Producto',

  /**
   * Obtener URL de imagen para un producto
   * @param {string} producto - ID del producto
   * @returns {string|null} URL de imagen o null
   */
  getImageUrl(producto) {
    const key = this._normalizeKey(producto);
    return this.IMAGES[key] || null;
  },

  /**
   * Obtener color de fondo para un producto
   * @param {string} producto - ID del producto
   * @returns {string} Color hexadecimal
   */
  getColor(producto) {
    const key = this._normalizeKey(producto);
    return this.COLORS[key] || this.DEFAULT_COLOR;
  },

  /**
   * Obtener nombre legible del producto
   * @param {string} producto - ID del producto
   * @returns {string} Nombre del producto
   */
  getName(producto) {
    const key = this._normalizeKey(producto);
    return this.NAMES[key] || this.DEFAULT_NAME;
  },

  /**
   * Obtener icono SVG del producto
   * @param {string} producto - ID del producto
   * @returns {string} SVG como string
   */
  getIcon(producto) {
    const key = this._normalizeKey(producto);
    return this.ICONS[key] || this.ICONS.premium;
  },

  /**
   * Obtener todos los datos de imagen para un producto
   * @param {string} producto - ID del producto
   * @returns {Object} { imageUrl, color, name, icon }
   */
  getProductData(producto) {
    return {
      imageUrl: this.getImageUrl(producto),
      color: this.getColor(producto),
      name: this.getName(producto),
      icon: this.getIcon(producto)
    };
  },

  /**
   * Configurar URL de imagen para un producto
   * Útil para configurar dinámicamente desde theme settings
   * @param {string} producto - ID del producto
   * @param {string} url - URL de la imagen
   */
  setImageUrl(producto, url) {
    const key = this._normalizeKey(producto);
    if (this.IMAGES.hasOwnProperty(key)) {
      this.IMAGES[key] = url;
      if (window.QUOTER_DEBUG) console.log('[ProductImages] Imagen configurada para ' + key + ':', url);
    }
  },

  /**
   * Normalizar key del producto (delega a QUOTER_CONFIG.normalizeProductId)
   * @private
   */
  _normalizeKey(producto) {
    if (window.QUOTER_CONFIG) return window.QUOTER_CONFIG.normalizeProductId(producto);
    return producto ? producto.toLowerCase().trim() : 'premium';
  }
};

// Exportar globalmente
if (typeof window !== 'undefined') {
  window.QuoterProductImages = QuoterProductImages;
}
